import Splide from '@splidejs/splide';
import { getInStockRecommendations, getShopifyInStockRecommendations } from '../services/recommendation.service';
import '../../styles/web-components/recommendations-carousel.scss';
import { DESKTOP_WIDTH, TABLET_WIDTH } from '../constants/devices.constants';
import { withoutTrailingZeros, createSrcset } from '../helpers/utils';
const MAX_RECOMMENDATIONS = 6;
const defaultCarouselOptions = {
    mediaQuery: "min",
    gap: "12px",
    perPage: 2,
    arrows: true,
    pagination: false,
    padding: { right: "15%" },
    breakpoints: {
        [TABLET_WIDTH]: {
            perPage: 3
        },
        [DESKTOP_WIDTH]: {
            destroy: true
        }
    }
};
const template = `
  <h2 class="recommendations-carousel__title">${window.UNTUCKitConfig.inStockRecommendationTitle}</h2>
  <section class="splide recommendations-carousel__slider" aria-label="In-Stock Recommendations" data-recs-source="${window.UNTUCKitConfig.productRecommendationsSource}">
    <loading-spinner></loading-spinner>
    <div class="splide__track">
      <ul class="splide__list"></ul>
    </div>
  </section>
`;
class RecommendationsCarousel extends HTMLElement {
    carousel;
    constructor() {
        super();
    }
    async connectedCallback() {
        this.innerHTML = template;
        const size = this.getAttribute('size');
        const fit = this.getAttribute('fit');
        const gender = this.getAttribute('gender');
        try {
            let recItems = [];
            if (window.UNTUCKitConfig.productRecommendationsSource === 'shopify') {
                const productId = this.getAttribute('productId');
                const recs = await getShopifyInStockRecommendations(productId, size, fit);
                recItems = recs?.slice(0, MAX_RECOMMENDATIONS).filter((r) => r.tags.includes(`department:${gender}`)).map((r) => ({
                    url: r.url,
                    image_url: r.featured_image,
                    name: r.title,
                    sku: r.matched_variant_sku,
                    sale: r.compare_at_price ? withoutTrailingZeros((r.price) / 100) : '',
                    original_price: withoutTrailingZeros((r.compare_at_price || r.price) / 100),
                    product_id: r.id,
                    show_in_stock_message: r.matched_variant_sku ? true : false,
                }));
            }
            else {
                const sku = this.getAttribute('sku');
                const recs = await getInStockRecommendations({ size, fit, gender, sku }, { maxProducts: MAX_RECOMMENDATIONS });
                recItems = recs?.slots?.map((s) => ({
                    url: s.item.url,
                    image_url: s.item.image_url,
                    name: s.item.name,
                    sku: s.item.sku,
                    sale: s.item.sale,
                    original_price: s.item.original_price,
                    product_id: s.item.group_id,
                    show_in_stock_message: false
                }));
            }
            if (!recItems?.length) {
                this.displayError();
                return;
            }
            const currencySymbol = window.UNTUCKitConfig.cartCurrencySymbol;
            const slideHtmlString = recItems.reduce((acc, rec) => {
                let recUrl = rec.url;
                if (rec.sku) {
                    recUrl = `${rec.url}${rec.url.includes('?') ? '&' : '?'}${`sku=${rec.sku}`}`;
                }
                const salePrice = `${currencySymbol}${rec.sale}`;
                const regularPrice = `${currencySymbol}${rec.original_price}`;
                const imageUrl = `${rec.image_url}${rec.image_url.includes('?') ? '&' : '?'}width=280`;
                const imageSrcset = createSrcset(rec.image_url, [280, 400, 600]);
                const htmlString = `
            <li class="splide__slide recommendations-carousel__slide">
              <a href="${recUrl}">
                <img class="recommendations-carousel__image" width="140" height="167" alt="${rec.name}" src="${imageUrl}" srcset="${imageSrcset}" sizes="(min-width: 1024px) 140px, (min-width: 768px) calc(28vw - 8px), calc(42.5vw - 6px)"/>
                <div class="recommendations-carousel__info">
                  <p class="recommendations-carousel__name">${rec.name}</p>
                  <p class="recommendations-carousel__price" data-product-id="${rec.product_id}">
                    <span class="recommendations-carousel__regular-price ${rec.sale ? 'recommendations-carousel__regular-price--sale' : ''}" aria-label="regular price ${regularPrice}">${regularPrice}</span>
                    <span class="recommendations-carousel__sale-price ${rec.sale ? '' : 'ADA__visually-hidden'}" aria-label="sale price ${salePrice}">${salePrice}</span>
                    ${rec.show_in_stock_message ? '<p class="recommendations-carousel__in-stock-message"><span class="inventory-level__indicator inventory-level__indicator--inStock" aria-hidden="true"></span>In Your Size</p>' : ''}
                  </p>
                </div>
              </a>
            </li>
          `;
                return acc + htmlString;
            }, '');
            this.querySelector('.splide__list').innerHTML = slideHtmlString;
            this.carousel = new Splide(this.querySelector('.splide'), defaultCarouselOptions).mount();
        }
        catch (error) {
            console.error(error);
            this.displayError();
        }
        finally {
            this.hideLoader();
        }
    }
    disconnectedCallback() {
        if (this.carousel) {
            this.carousel.destroy();
        }
    }
    displayError(msg = "Sorry, we're unable to provide recommendations at the moment.") {
        const container = this.querySelector(".recommendations-carousel__slider");
        if (container) {
            container.innerHTML = `<p class="recommendations-carousel__error-message">${msg}</p>`;
            container.style.visibility = "visible";
        }
    }
    hideLoader() {
        const spinner = this.querySelector('loading-spinner');
        if (spinner) {
            spinner.setAttribute("show", "false");
        }
    }
}
if (!window.customElements.get("recommendations-carousel")) {
    customElements.define("recommendations-carousel", RecommendationsCarousel);
}
